// What the page's windows show (inventory, character sheet, workbench, bags, shops, NPC dialogue, quests, trade, the
// world map), with the rules the windows apply, and their tooltips.
import { Binding, ContainerId, GameConstants, NoticeKind, OffhandKind, QuestObjectiveKind, QuestStatus, TalentArea, TalentTier, TileEntityType, enumName } from '../../../shared/src/constants';
import { content, type ItemDefinition, type QuestDefinition, type Recipe } from '../core/content';
import { InteractionController } from '../core/interaction';
import { feetTile } from '../../../shared/src/motor';
import { Money, Trade, Vendor, maxCraftBatch, recipeName, recipesFor, tryCraft } from '../core/rules/economy';
import {
  Defense, Equipment, Inventory, ToolBelt, allItems, consumableCooldownSeconds, isArmor, isEmpty, isJewelry, isOffhand, isTool, jewelrySlotKind,
  offhandFits, stacksWith, type ItemStack,
} from '../core/rules/items';
import { Leveling, describeTask, progressOf } from '../core/rules/progress';
import { TalentRules, TalentStats, allocateProblem, describeEffect, effectGood, refundProblem } from '../core/rules/talents';
import { Tip, UiColors, equippedFor, itemLines, nothingWorn, qualityName } from '../core/rules/tooltip';
import { TileEntities, tileEntityDef } from '../../../shared/src/rules/world';
import { collectLostSeconds, type ClientSession } from '../core/session';
import { game, outfitSlots } from './game';

const S = (): ClientSession | null => game.session;
const C = UiColors;
const stackJson = (s: ItemStack) => ({ item: s.item, count: s.count, bound: s.bound });
const tipJson = (tip: Tip, title: string, color: string) => ({ title, color, lines: tip.lines });
/** An item's tooltip: its name, with its rarity on the right. */
const itemTipJson = (tip: Tip, def: ItemDefinition) => ({ ...tipJson(tip, def.name, C.quality(def.quality)), rarity: qualityName(def.quality) });

// ---- inventory -------------------------------------------------------------------------------------------------

export function inventory(): object {
  const s = S();
  if (!s) return {};
  const inv = s.inventory;
  return {
    main: inv.main.map(stackJson), belt: inv.belt.map(stackJson), armor: inv.armor.map(stackJson), money: s.money,
    armorValue: Equipment.armorValue(inv), cooldown: Math.max(0, s.consumableReadyAt - s.time), cooldownTotal: consumableCooldownSeconds,
    // The offhand's ghost is what the weapon takes, or any offhand while it takes nothing (no weapon, two-handed).
    ghosts: ToolBelt.slots.map((k, i) => (i === ToolBelt.offhand
      ? allItems().find(d => offhandFits(inv.weaponItem, d.id, content())) ?? allItems().find(isOffhand)
      : allItems().find(d => d.tool === k || (isJewelry(d) && jewelrySlotKind(d.jewelry) === k)))?.id ?? 0),
    armorGhosts: Equipment.slots.map(k => allItems().find(d => d.armor === k)?.id ?? 0),
  };
}

/** Whether the stack in (fromC, from) could go into (toC, to): its kind fits, or it merges. */
export function canPlace(fromC: ContainerId, from: number, toC: ContainerId, to: number): boolean {
  const s = S();
  if (!s) return false;
  const st = s.inventory.get(fromC, from);
  return (Inventory.canPlace(toC, to, st, content(), s.inventory.weaponItem) && !s.inventory.uniqueClash(fromC, from, toC, to))
    || stacksWith(s.inventory.get(toC, to), st);
}

function wouldBind(st: ItemStack, c: ContainerId, index: number): boolean {
  const weapon = S()?.inventory.weaponItem ?? 0;
  return c !== ContainerId.Main && !isEmpty(st) && !st.bound && content().item(st.item)?.binding === Binding.OnEquip && Inventory.canPlace(c, index, st, content(), weapon);
}

/** The item a move would soulbind (either stack: a swap can put the other into equipment), or 0. */
export function moveBinds(fromC: ContainerId, from: number, toC: ContainerId, to: number): number {
  const s = S();
  if (!s) return 0;
  const moving = s.inventory.get(fromC, from), other = s.inventory.get(toC, to);
  return wouldBind(moving, toC, to) ? moving.item : wouldBind(other, fromC, from) ? other.item : 0;
}

/** The item shift-click (equip from the bag) would soulbind, or 0. */
export function quickMoveBinds(c: ContainerId, index: number): number {
  const s = S();
  if (!s || c !== ContainerId.Main) return 0;
  const st = s.inventory.get(ContainerId.Main, index);
  const def = content().item(st.item);
  if (!def) return 0;
  const target = s.inventory.equipSlot(def);
  return target && wouldBind(st, ...target) ? st.item : 0;
}

/**
 * An item's tooltip. compare: it's in the bag (or a shop, a bag...), so Shift compares gear with what's worn in its
 * slot (shown alongside, as "worn"); without Shift, gear offers to.
 */
export function itemTip(item: number, bound: boolean, count: number, hint: string, compare: boolean, shift: boolean): object {
  const def = content().item(item), s = S();
  if (!def || !s) return {};
  const equipped = compare ? equippedFor(s.inventory, def) : null;
  const comparing = equipped !== null && shift;
  const wornDef: ItemDefinition | null = comparing && !isEmpty(equipped!.worn) ? content().item(equipped!.worn.item) ?? null : null;
  const result: Record<string, unknown> = itemTipJson(
    itemLines(def, hint, bound, comparing ? wornDef ?? nothingWorn : null, equipped !== null && !comparing, count), def);
  if (comparing) {
    const { worn, slot } = equipped!;
    if (wornDef) {
      const tip = itemLines(wornDef, '', worn.bound);
      tip.lines.unshift({ t: `Equipped (${slot.toLowerCase()})`, c: C.dim, vc: C.dim });
      result.worn = itemTipJson(tip, wornDef);
    } else {
      result.worn = tipJson(new Tip().line(`${slot}: ${isArmor(def) ? 'basic cloth' : 'empty'}`, C.dim), 'Nothing equipped', C.dim);
    }
  }
  return result;
}

// ---- character sheet -------------------------------------------------------------------------------------------

export function character(): object {
  const s = S();
  if (!s) return {};
  const inv = s.inventory, b = s.talentBonuses;
  const next = Leveling.expToNext(s.level), unspent = s.unspentTalentPoints;
  const regen = (perSecond: number) => `${Number.isInteger(perSecond) ? perSecond : perSecond.toFixed(1)} per second`;
  // Health: how the max adds up.
  const fromLevel = (s.level - 1) * Leveling.healthPerLevel, base = Leveling.baseHealth;
  const healthTip = tipJson(new Tip().line('Base', C.text, `${base}`).plus(`Level ${s.level}`, fromLevel, C.text)
    .plus('Talents', s.maxHealth - base - fromLevel, C.text).line('Max health', C.accent, `${s.maxHealth}`).gap()
    .line('Regeneration', C.text, regen(TalentStats.healthRegen(b)), C.bonus).gap()
    .line('Recovers over time, and fully when you level up.', C.description).gap()
    .line(`+${Leveling.healthPerLevel} per level; talents (N) add more`, C.hint),
  `Health ${s.health} / ${s.maxHealth}`, C.accent);

  // Mana: the same.
  const mana = Math.floor(s.mana), manaFromLevel = (s.level - 1) * Leveling.manaPerLevel, manaBase = Leveling.baseMana;
  const manaTip = tipJson(new Tip().line('Base', C.text, `${manaBase}`).plus(`Level ${s.level}`, manaFromLevel, C.text)
    .plus('Talents', s.maxMana - manaBase - manaFromLevel, C.text).line('Max mana', C.accent, `${s.maxMana}`).gap()
    .line('Regeneration', C.text, regen(TalentStats.manaRegen(b)), C.bonus).gap()
    .line('Staves spend it on each spell. It comes back steadily, and fully when you level up.', C.description).gap()
    .line(`+${Leveling.manaPerLevel} per level; talents (N) add more`, C.hint),
  `Mana ${mana} / ${s.maxMana}`, C.accent);

  const armor = new Tip();
  inv.armor.forEach((piece, i) => {
    const def = isEmpty(piece) ? undefined : content().item(piece.item);
    armor.plus(`${Equipment.slotName(i)}: ${def?.name ?? 'cloth'}`, def?.armorValue ?? 0, def ? C.text : C.muted);
  });
  for (const slot of [ToolBelt.offhand, ToolBelt.necklace, ...ToolBelt.rings]) {
    const worn = inv.belt[slot], def = isEmpty(worn) ? undefined : content().item(worn.item);
    if (def?.armorValue) armor.plus(`${ToolBelt.slotName(slot)}: ${def.name}`, def.armorValue, C.text);
  }
  const gear = Equipment.armorValue(inv), armorValue = Math.round(TalentStats.armor(gear, b));
  const blockArmor = Math.round(TalentStats.blockArmor(Equipment.blockArmor(inv), b));
  const percent = (a: number) => `${Math.round(Defense.reduction(a) * 100)}%`;
  if (armorValue !== gear) armor.plus('Talents', armorValue - gear, C.text);
  armor.line('Armor', C.accent, `${armorValue}`).line('Damage taken', C.text, `-${percent(armorValue)}`, C.bonus);
  if (Equipment.shield(inv)) armor.gap().plus(`Blocking: ${content().item(Equipment.shield(inv))?.name}`, blockArmor, C.text)
    .line('Damage taken while blocking', C.text, `-${percent(armorValue + blockArmor)}`, C.bonus);
  armor.gap().line('Armour lessens the damage of every hit. A shield adds more while you block (hold right-click), but each hit blocked costs stamina, and one without the stamina for it stuns you.', C.description).gap()
    .line('Craft armour at a workbench', C.hint);

  // Talents: every total, in the tree's order of stats.
  const totals = new Tip();
  b.forEach((value, stat) => { if (value) { const e = { stat, value }; totals.line(describeEffect(e), effectGood(e) ? C.bonus : C.worse); } });
  if (!totals.lines.length) totals.line('No talents yet.', C.muted);
  totals.gap().line(unspent > 0 ? `${unspent} point${unspent === 1 ? '' : 's'} to spend: press N` : 'Press N to see the talent tree', unspent > 0 ? C.bonus : C.hint);
  return {
    name: s.local.name, appearance: s.local.appearance, level: s.level, exp: s.exp, next, hp: s.health, maxHp: s.maxHealth,
    mana, maxMana: s.maxMana, armor: armorValue, unspent, spent: s.talents.length, healthTip, manaTip,
    armorTip: tipJson(armor, `Armor ${armorValue}`, C.accent), talentTip: tipJson(totals, 'Talents', C.accent),
  };
}

// ---- talent tree -----------------------------------------------------------------------------------------------

const areaColor: Record<TalentArea, string> = {
  [TalentArea.None]: '#d8c8a8', [TalentArea.Tank]: '#7fa7d9', [TalentArea.Support]: '#7fd99a', [TalentArea.Melee]: '#e0675a',
  [TalentArea.Ranged]: '#e0c25a', [TalentArea.Spell]: '#b88ae6',
};
const tierName: Record<TalentTier, string> = {
  [TalentTier.Start]: 'Start', [TalentTier.Small]: 'Talent', [TalentTier.Notable]: 'Notable', [TalentTier.Keystone]: 'Keystone',
};

/** The tree as the talent window draws it: each node, whether it's taken or can be, and its tooltip. */
export function talents(): object {
  const s = S();
  if (!s) return {};
  const c = content(), taken = new Set(s.talents), auras = new Set(s.auras), unspent = s.unspentTalentPoints;
  const cost = TalentRules.refundCost(s.level), canPay = s.money >= cost;
  const nodes = [...c.talents.values()].map(n => {
    const has = taken.has(n.id) || n.tier === TalentTier.Start;
    const next = !has && n.links.some(l => l === c.talentStart || taken.has(l));
    const state = has ? 'taken' : next ? (unspent > 0 ? 'open' : 'next') : 'locked';
    const tip = new Tip();
    tip.line(`${tierName[n.tier]}${n.area ? ` - ${TalentArea[n.area]}` : ''}${n.aura ? ' aura' : ''}`, C.dim);
    for (const e of n.effects) tip.line(describeEffect(e), effectGood(e) ? C.bonus : C.worse);
    if (n.aura) tip.line(`Also reaches party members within ${TalentRules.auraRangeTiles} tiles (more with aura range)`, C.text);
    if (n.description) tip.gap().line(n.description, C.description);
    if (auras.has(n.id) && !has) tip.gap().line('A party member nearby shares this aura with you.', C.bonus);
    tip.gap();
    if (n.tier === TalentTier.Start) tip.line('Free: every tree grows from here.', C.hint);
    else if (has) {
      const problem = refundProblem(c, s.talents, n.id);
      tip.line(problem ?? `Right-click to give back for ${Money.formatLong(cost)}`, problem || !canPay ? C.muted : C.hint);
    } else if (state === 'open') tip.line(`Click to learn (${unspent} point${unspent === 1 ? '' : 's'} left)`, C.bonus);
    else if (state === 'next') tip.line(s.level < Leveling.maxLevel ? `No points left: the next comes at level ${s.level + 1}` : 'No points left', C.muted);
    else tip.line('Learn a talent next to it first.', C.muted);
    const color = n.tier === TalentTier.Start ? areaColor[TalentArea.None] : areaColor[n.area];
    return { id: n.id, name: n.name, x: n.x, y: n.y, tier: n.tier, area: n.area, color, icon: n.icon, aura: n.aura, links: n.links, state,
      shared: auras.has(n.id), tip: tipJson(tip, n.name, n.tier >= TalentTier.Notable ? C.accent : C.text) };
  });
  return { unspent, spent: s.talents.length, points: TalentRules.pointsForLevel(s.level), refundCost: cost, level: s.level,
    areas: [TalentArea.Tank, TalentArea.Melee, TalentArea.Ranged, TalentArea.Spell, TalentArea.Support].map(a => ({ name: TalentArea[a], color: areaColor[a] })),
    nodes };
}
export function talentAction(node: number, refund: boolean): void {
  const s = S();
  if (!s) return;
  const problem = refund ? refundProblem(content(), s.talents, node) ?? (s.money < TalentRules.refundCost(s.level) ? `Giving back a talent costs ${Money.formatLong(TalentRules.refundCost(s.level))}.` : null)
    : allocateProblem(content(), s.level, s.talents, node);
  if (problem) { s.notices.push({ kind: NoticeKind.Error, text: problem, time: s.time }); return; }
  s.sendTalentAction(node, refund);
}

// ---- workbench -------------------------------------------------------------------------------------------------

function maxCraftable(r: Recipe, inv: Inventory): number {
  const sim = inv.clone();
  let n = 0;
  while (n < maxCraftBatch && tryCraft(r, sim) === null) n++;
  return n;
}

/** A station's recipes: what each makes, how many could be crafted now, its tab and armour set, and the ingredients. */
export function crafting(stationId: number): object {
  const s = S();
  const station = s?.world.tileEntities.get(stationId);
  if (!s || !station) return { gone: true };
  const inv = s.inventory;
  return {
    gone: !TileEntities.canInteract(station, s.local.x, s.local.y, s.local.state.facing),
    name: tileEntityDef(station.type)?.name ?? '',
    recipes: recipesFor(station.type).map(r => {
      const def = content().item(r.output.item);
      const tab = def && (isArmor(def) || def.offhand === OffhandKind.Shield) ? 'Armour' : def && (isTool(def) || isOffhand(def)) ? 'Tools' : 'Materials';
      const set = def && isArmor(def) && def.look.length > 0 ? def.name.split(' ')[0] : null;
      return {
        id: r.id, item: r.output.item, count: r.output.count, name: recipeName(r), tab, set, max: maxCraftable(r, inv),
        inputs: r.inputs.map(i => ({ item: i.item, count: i.count, have: inv.countInBag(i.item), name: content().item(i.item)?.name ?? String(i.item) })),
      };
    }),
  };
}

// ---- loot bags, shops ------------------------------------------------------------------------------------------

export function bag(id: number): object {
  const s = S();
  const b = s?.world.bags.get(id);
  if (!s || !b) return { gone: true };
  return { gone: Math.hypot(b.x - s.local.x, b.y - s.local.y) > InteractionController.bagReachPixels + 12, contents: b.contents.map(stackJson) };
}

function vendorAt(s: ClientSession, npcEntity: number) {
  const e = s.world.tileEntities.get(npcEntity);
  const npc = e && e.type === TileEntityType.Npc ? content().npc(e.data) : undefined;
  return e && npc && npc.stock.length > 0 ? { e, npc } : null;
}

export function vendor(npcEntity: number): object {
  const s = S();
  const v = s && vendorAt(s, npcEntity);
  if (!s || !v) return { gone: true };
  return {
    gone: TileEntities.distanceTo(v.e, s.local.x, s.local.y) > TileEntities.clientReachPixels + 12, name: v.npc.name, money: s.money,
    junk: s.inventory.main.filter(Vendor.isJunk).reduce((n, st) => n + Vendor.sellValue(st), 0), maxBuy: Vendor.maxBuyCount,
    stock: v.npc.stock.map(o => ({ item: o.item, price: o.price, priceText: Money.format(o.price) })),
  };
}

/** Buys from a shop after the client's own checks (money, space). */
export function vendorBuy(npcEntity: number, index: number, many: boolean): boolean {
  const s = S();
  const v = s && vendorAt(s, npcEntity);
  if (!s || !v || index < 0 || index >= v.npc.stock.length) return false;
  const offer = v.npc.stock[index];
  const count = many ? Vendor.maxBuyCount : 1;
  const affordable = offer.price <= 0 ? count : Math.min(count, Math.floor(s.money / offer.price));
  const error = affordable <= 0 ? `You can't afford that (${Money.formatLong(offer.price)}).`
    : s.inventory.spaceFor(offer.item) <= 0 ? 'Your inventory is full.' : null;
  if (error) {
    s.notices.push({ kind: NoticeKind.Error, text: error, time: s.time });
    return false;
  }
  s.sendVendorBuy(v.e.id, index, offer.item, affordable);
  return true;
}

export function vendorSell(npcEntity: number, c: ContainerId, index: number): boolean {
  const s = S();
  const e = s?.world.tileEntities.get(npcEntity);
  if (!s || !e) return false;
  const st = s.inventory.get(c, index);
  if (isEmpty(st)) return false;
  if (Vendor.sellValue(st) <= 0) {
    s.notices.push({ kind: NoticeKind.Error, text: `${content().npc(e.data)?.name ?? 'The vendor'} isn't interested in that.`, time: s.time });
    return false;
  }
  s.sendVendorSell(e.id, c, index, st.item);
  return true;
}

export const sellValue = (c: ContainerId, index: number): number => {
  const s = S();
  return s ? Vendor.sellValue(s.inventory.get(c, index)) : 0;
};

// ---- NPC dialogue and quests -----------------------------------------------------------------------------------

function objectives(s: ClientSession, def: QuestDefinition): object[] {
  const stored = s.activeQuests.get(def.id);
  return def.objectives.map((o, i) => {
    const have = stored ? progressOf(o, i < stored.length ? stored[i] : 0, s.inventory) : 0;
    const l = s.collectLost.get(`${def.id}:${i}`);
    const lost = stored && l && s.time - l.at < collectLostSeconds ? l.lost : 0;
    return { text: describeTask(o), have: Math.min(have, o.count), need: o.count, count: o.kind !== QuestObjectiveKind.Talk, lost };
  });
}

const rewards = (def: QuestDefinition) => ({
  copper: def.rewardCopper, exp: def.rewardExp, rewards: def.rewards.map(r => ({ item: r.item, count: r.count })),
});

/** The NPC the player is talking to and its quests (once the server has answered), or gone when they walked away. */
export function dialogue(npcEntity: number): object {
  const s = S();
  const e = s?.world.tileEntities.get(npcEntity);
  const npc = e && e.type === TileEntityType.Npc ? content().npc(e.data) : undefined;
  if (!s || !e || !npc) return { gone: true };
  const answered = s.dialogue !== null && s.dialogue.npc === npc.id;
  return {
    gone: TileEntities.distanceTo(e, s.local.x, s.local.y) > TileEntities.clientReachPixels + 12,
    name: npc.name, appearance: npc.appearance, greeting: npc.greeting, outfit: outfitSlots(npc.outfit), answered,
    quests: answered ? s.dialogue!.options.flatMap(o => {
      const def = content().quest(o.quest);
      return def && o.status !== QuestStatus.Completed ? [{
        id: def.id, title: def.title, status: enumName(QuestStatus, o.status).toLowerCase(), description: def.description,
        objectives: objectives(s, def), ...rewards(def),
      }] : [];
    }) : [],
  };
}

/** Active quests (the journal, J, and the tracker): objectives, or where to hand them in; story and rewards. */
export function quests(): object[] {
  const s = S();
  if (!s) return [];
  return [...s.activeQuests.keys()].sort((a, b) => a - b).flatMap(id => {
    const def = content().quest(id);
    return def ? [{
      id: def.id, title: def.title, ready: s.questStatus(def) === QuestStatus.Completable, giver: content().npc(def.giver)?.name ?? '',
      description: def.description, objectives: objectives(s, def), ...rewards(def),
    }] : [];
  });
}

// ---- trade -----------------------------------------------------------------------------------------------------

export function trade(): object {
  const s = S();
  const t = s?.trade;
  if (!s || !t) return { open: false };
  return {
    open: true, partner: t.partner, revision: t.revision, mineAccepted: t.mineAccepted, theirsAccepted: t.theirsAccepted,
    myCopper: t.mine.copper, theirCopper: t.theirCopper, canAccept: !t.mineAccepted && s.time - s.tradeChangedAt >= 1.0,
    acceptIn: Math.max(0, 1.0 - (s.time - s.tradeChangedAt)), maxSlots: Trade.maxSlots,
    mine: t.mine.slots.map(o => ({ slot: o.slot, item: o.item, count: o.count })), theirs: t.theirs.map(stackJson),
  };
}

/** Adds a bag stack to the offer, after the client's own checks. */
export function tradeOffer(c: ContainerId, index: number): void {
  const s = S();
  const t = s?.trade;
  if (!s || !t) return;
  const st = s.inventory.get(c, index);
  if (isEmpty(st) || t.mine.slots.some(x => x.slot === index && c === ContainerId.Main)) return;
  const error = c !== ContainerId.Main ? 'Put it in your bag first to trade it.'
    : st.bound ? `${content().item(st.item)?.name ?? 'That'} is soulbound: it can't be traded.`
    : t.mine.slots.length >= Trade.maxSlots ? `You can offer at most ${Trade.maxSlots} stacks.` : null;
  if (error) { s.notices.push({ kind: NoticeKind.Error, text: error, time: s.time }); return; }
  s.sendTradeOffer(Trade.with(t.mine, { slot: index, item: st.item, count: st.count }));
}

export function tradeRemove(slot: number): void {
  const s = S();
  if (s?.trade) s.sendTradeOffer(Trade.without(s.trade.mine, slot));
}

/** Offers money typed as "150", "2s" or "1g 50s". Returns an error, or null when sent. */
export function tradeMoney(text: string): string | null {
  const s = S();
  if (!s?.trade) return 'No trade open.';
  text = text.trim() || '0';
  const copper = Money.parse(text);
  if (copper === null) return 'Type an amount like 150, 2s or 1g 50s.';
  if (copper > s.money) return "You don't have that much money.";
  s.sendTradeOffer(Trade.withCopper(s.trade.mine, copper));
  return null;
}

// ---- world map -------------------------------------------------------------------------------------------------

let mapFor: unknown = null;

/** The explored map: size, chunks changed since the last call (all of them after a zone change, with "reset"), cave entrances, you, your party. */
export function map(): object {
  const s = S();
  if (!s) return {};
  const memory = s.mapMemory;
  const reset = memory !== mapFor;
  if (reset) {
    mapFor = memory;
    memory.markAllDirty();
  }
  const ts = GameConstants.tileSize;
  return {
    reset, zone: s.world.zoneName, x: s.local.x / ts, y: s.local.y / ts, tx: feetTile(s.local.x, s.local.y)[0], ty: feetTile(s.local.x, s.local.y)[1], f: s.local.state.facing, explored: memory.chunks.size,
    dirty: memory.takeDirty(),
    entrances: [...memory.caveEntrances].map(k => k.split(',').map(Number)),
    party: s.partyMembers.flatMap(m => {
      const pp = s.partyPositions.get(m.name.toLowerCase());
      if (m.name.toLowerCase() === s.local.name.toLowerCase() || !pp) return [];
      const here = pp.pos.zoneId === s.world.zoneId && pp.pos.instanceId === s.world.instanceId;
      return [{ name: m.name, here, where: m.zoneName || 'elsewhere', x: pp.pos.x / ts, y: pp.pos.y / ts }];
    }),
  };
}

/** One explored chunk as 32x32 bytes (ground in the low 4 bits, a coarse feature in the high 4). */
export function mapChunk(cx: number, cy: number): Uint8Array {
  return S()?.mapMemory.chunks.get(`${cx},${cy}`)?.data ?? new Uint8Array(0);
}

